export const API_BASE = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:4000';

/** Fetch a file from the API (CSV export) and hand it to the browser as a download. */
export async function apiDownload(path: string, fallbackName: string): Promise<void> {
  const headers = new Headers();
  const token = loadStoredToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const res = await fetch(`${API_BASE}${path}`, { headers });
  if (!res.ok) throw new ApiError(res.status, `Download failed (HTTP ${res.status})`);
  const blob = await res.blob();
  const name = res.headers.get('Content-Disposition')?.match(/filename="([^"]+)"/)?.[1] ?? fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
  }
}

let accessToken: string | null = null;
const TOKEN_KEY = 'mana.accessToken';

export function loadStoredToken(): string | null {
  if (typeof window === 'undefined') return null;
  if (accessToken) return accessToken;
  const raw = window.localStorage.getItem(TOKEN_KEY);
  if (raw) accessToken = raw;
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
  if (typeof window === 'undefined') return;
  if (token) window.localStorage.setItem(TOKEN_KEY, token);
  else window.localStorage.removeItem(TOKEN_KEY);
}

export async function api<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const headers = new Headers(init.headers ?? {});
  headers.set('Accept', 'application/json');
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const token = loadStoredToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  const text = await res.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = text;
  }
  if (!res.ok) {
    // Build the most human-readable message we can, in priority order:
    //   1. the server's explicit `message` (e.g. signup email_taken sentence),
    //   2. the first Zod validation issue, so a 400 `validation_failed` reads as
    //      e.g. "Email must be at @manahomeloans.com." instead of a bare code,
    //   3. the machine `error` code, 4. the bare HTTP status.
    const obj = body && typeof body === 'object' ? (body as Record<string, unknown>) : null;
    const explicit =
      obj && typeof obj.message === 'string' && obj.message.trim() ? obj.message : null;
    const code = obj && 'error' in obj ? String(obj.error) : null;
    const msg = explicit ?? firstValidationMessage(obj) ?? code ?? `HTTP ${res.status}`;
    throw new ApiError(res.status, msg, body);
  }
  return body as T;
}

/** Pull the first readable message out of a Zod `flatten()` error body, if any. */
function firstValidationMessage(obj: Record<string, unknown> | null): string | null {
  const issues =
    obj && typeof obj.issues === 'object' && obj.issues !== null
      ? (obj.issues as { formErrors?: unknown; fieldErrors?: unknown })
      : null;
  if (!issues) return null;
  if (Array.isArray(issues.formErrors)) {
    const form = issues.formErrors.find((m): m is string => typeof m === 'string');
    if (form) return form;
  }
  if (issues.fieldErrors && typeof issues.fieldErrors === 'object') {
    for (const val of Object.values(issues.fieldErrors as Record<string, unknown>)) {
      if (Array.isArray(val)) {
        const first = val.find((m): m is string => typeof m === 'string');
        if (first) return first;
      }
    }
  }
  return null;
}
